import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { getQueryParamsAsObject } from "../js/helpers/url.utils";
import { keysToCamel } from "@/js/helpers/object.helper";
import { getStorageItem, setStorageItem } from "@/js/helpers/webstorage";

interface VisitParams extends VisitSiteParams, VisitSignupParams {}

/** site visit params (ex: /page?utm_source=google...) */
export interface VisitSiteParams {
   utmSource?: string;
   utmMedium?: string;
   utmCampaign?: string;
   utmContent?: string;
}

/** Sign Up specific params - most are from url query parameters (ex: /page?invitation_code=345&r=1234...) */
export interface VisitSignupParams {
   // Some Portals require an 'invite reg' code (which is not user-specific) to exist before a user can register
   invitationCode?: string;
   // Sometimes a user will register and should at that point be linked to a particular site
   studySiteCode?: string;
   // Some Portals require a registration code (which is unique to each user) to exist before a user can register
   registrationCode?: string;
}

interface AnalyticsData {
   // Used to track the study page that the user was on before they signed up (not a query param)
   studyId?: string;
}

const visitSiteLSKey = "visitSiteParams";
const visitSignupLSKey = "visitSignupParams";
const analyticsDataLSKey = "analyticsData";

export const useAppStore = defineStore("appStore", () => {
   // Define state for both parameter types
   const visitSiteParams = ref<VisitSiteParams>({});
   const visitSignupParams = ref<VisitSignupParams>({});
   const analyticsData = ref<AnalyticsData>({});

   // Computed to read states
   const readVisitSiteParams = computed<VisitSiteParams>(() => visitSiteParams.value);
   const readVisitSignupParams = computed<VisitSignupParams>(() => visitSignupParams.value);
   const readAnalyticsData = computed<AnalyticsData>(() => analyticsData.value);

   /** load site params from web storage into visitSiteParams */
   const loadSiteParams = () => {
      if (import.meta.env.SSR) return;

      const siteLSItems = getStorageItem("session", visitSiteLSKey) as VisitSiteParams;

      visitSiteParams.value = {
         ...(siteLSItems?.utmSource && { utmSource: siteLSItems.utmSource }),
         ...(siteLSItems?.utmMedium && { utmMedium: siteLSItems.utmMedium }),
         ...(siteLSItems?.utmCampaign && { utmCampaign: siteLSItems.utmCampaign }),
         ...(siteLSItems?.utmContent && { utmContent: siteLSItems.utmContent }),
      };
   };
   /** load signup params from web storage into visitSiteParams */
   const loadSignupParams = () => {
      if (import.meta.env.SSR) return;

      const signUpLSItems = getStorageItem("session", visitSignupLSKey) as VisitSignupParams;

      visitSignupParams.value = {
         ...(signUpLSItems?.registrationCode && { registrationCode: signUpLSItems.registrationCode }),
         ...(signUpLSItems?.invitationCode && { invitationCode: signUpLSItems.invitationCode }),
         ...(signUpLSItems?.studySiteCode && { studySiteCode: signUpLSItems.studySiteCode }),
      };
   };
   /** load analytics data from web storage into visitSiteParams */
   const loadAnalyticsData = () => {
      if (import.meta.env.SSR) return;

      const analyticsDataLSItems = getStorageItem("session", analyticsDataLSKey) as AnalyticsData;

      analyticsData.value = {
         ...(analyticsDataLSItems?.studyId && { studyId: analyticsDataLSItems.studyId }),
      };
   };

   /** save analytics data to the ref and web storage */
   function saveAnalyticsData(studyId: string) {
      if (!studyId) return;

      if (studyId) {
         analyticsData.value.studyId = studyId;
      }

      setStorageItem("session", analyticsDataLSKey, analyticsData.value);
   }

   // Load data from web storage
   // Note: This could get overwritten by the URL query parameter functions that run later
   loadSiteParams();
   loadSignupParams();
   loadAnalyticsData();

   /** Set the visit parameters from the URL query parameters (Site and Sign Up) */
   function setUrlQueryParams() {
      const queryParamsOrig = getQueryParamsAsObject();
      const queryParams: VisitParams = keysToCamel(queryParamsOrig);

      if (queryParams) {
         setVisitSiteParams(queryParams);
         setSignupParams(queryParams);
      }
   }

   function setVisitSiteParams(queryParams: VisitSiteParams) {
      const { utmSource, utmMedium, utmCampaign, utmContent } = queryParams;

      // if query parameters had any utm's, overwrite the entire visitSiteParams & session site visit params
      if (utmSource || utmMedium || utmCampaign || utmContent) {
         visitSiteParams.value = {
            ...(utmSource && { utmSource: utmSource }),
            ...(utmMedium && { utmMedium: utmMedium }),
            ...(utmCampaign && { utmCampaign: utmCampaign }),
            ...(utmContent && { utmContent: utmContent }),
         };
         setStorageItem("session", visitSiteLSKey, visitSiteParams.value);
      }
   }

   function setSignupParams(queryParams: VisitSignupParams) {
      const { registrationCode, invitationCode, studySiteCode } = queryParams;

      // overwrite any individual change in sign up params
      if (registrationCode || invitationCode || studySiteCode) {
         if (registrationCode) {
            visitSignupParams.value.registrationCode = registrationCode;
         }
         if (invitationCode) {
            visitSignupParams.value.invitationCode = invitationCode;
         }
         if (studySiteCode) {
            visitSignupParams.value.studySiteCode = studySiteCode;
         }

         setStorageItem("session", visitSignupLSKey, visitSignupParams.value);
      }
   }

   function deleteVisitWebStorage() {
      sessionStorage.removeItem(visitSiteLSKey);
      sessionStorage.removeItem(visitSignupLSKey);
      sessionStorage.removeItem(analyticsDataLSKey);
   }

   return {
      readVisitSiteParams,
      readVisitSignupParams,
      readAnalyticsData,
      saveAnalyticsData,
      setUrlQueryParams,
      deleteVisitWebStorage,
   };
});
